import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { usePageMetadata } from '../hooks/usePageMetadata';
import {
  MapPin,
  TrendingUp,
  FileText,
  DollarSign,
  Clock,
  Target
} from 'lucide-react';
import InvestmentModal from '../components/investment/InvestmentModal';
import { useGetProjectStatusQuery } from '../store/slices/apiSlice';
import Navbar from '../components/common/Navbar';
import { API_BASE_URL } from '../config/api';

const ProjectDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state: RootState) => state.auth);
  const isAuthenticated = !!token;

  // Set page metadata
  usePageMetadata('projectDetails');

  // Get project data from location state (passed from navigation)
  const project = location.state?.project;
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Fetch project status if project_id is available
  const { data: projectStatusData } = useGetProjectStatusQuery(
    project?.project_id || 0,
    { skip: !project?.project_id || !isAuthenticated }
  );

  // Use project status data if available, otherwise use project data from state
  const projectStatus = projectStatusData?.data;
  const canInvest = projectStatus?.can_invest ?? (project?.status === 'active' && !project?.is_funded);

  // If no project data is passed via state, redirect to projects page
  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
          <p className="text-gray-600 mb-6">
            Please navigate to this page from the projects list.
          </p>
          <Link to="/projects" className="text-primary-600 hover:text-primary-800">
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Use project status data if available, otherwise fall back to project data
  const currentAmountRaised = projectStatus?.amount_raised
    ? parseFloat(projectStatus.amount_raised)
    : parseFloat(project?.amount_raised || '0');
  const currentFundingGoal = projectStatus?.funding_goal
    ? parseFloat(projectStatus.funding_goal)
    : parseFloat(project?.funding_goal || '0');
  const currentIsFunded = projectStatus?.is_funded ?? project?.is_funded;

  const progressPercentage = currentAmountRaised && currentFundingGoal
    ? (currentAmountRaised / currentFundingGoal) * 100
    : 0;
  const remainingAmount = currentFundingGoal - currentAmountRaised;
  const isFullyFunded = currentIsFunded || progressPercentage >= 100;

  // Get currency symbol based on project's currency
  const getCurrencySymbol = (symbol: string | null | undefined) => {
    if (!symbol) return '$'; // Default to USD symbol
    return symbol;
  };

  const formatCurrency = (amount: number) => {
    const currencySymbol = getCurrencySymbol(project.currency_symbol);

    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount).replace(/^/, currencySymbol);
  };

  const handleInvestClick = () => {
    if (!isAuthenticated) {
      if (window.innerWidth < 1024) {
        window.location.href = 'https://app.diasporawise.com';
      } else {
        navigate('/');
      }
      return;
    }
    setShowInvestmentModal(true);
  };

  // Helper function to get full image URL
  const getImageUrl = (imageUrl: string | null | undefined) => {
    if (!imageUrl) return null;
    const APP_URL = API_BASE_URL;
    // If already a full URL, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    // If starts with /uploads, prepend base URL
    if (imageUrl.startsWith('/uploads')) {
      return `${APP_URL}${imageUrl}`;
    }
    // Otherwise, assume it's in uploads folder
    return `${APP_URL}/uploads/${imageUrl}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {/* Image Gallery */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-lg overflow-hidden">
              <div className="relative">
                {project.images && project.images.length > 0 ? (
                  (() => {
                    const imageUrl = getImageUrl(project.images[selectedImageIndex]?.image_url);
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={project.title}
                        className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          // Try fallback placeholder
                          target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                          target.className = 'w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover';
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-sm sm:text-base">No image available</span>
                      </div>
                    );
                  })()
                ) : (
                  <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-400 text-sm sm:text-base">No image available</span>
                  </div>
                )}
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${project.status === 'active' ? 'bg-success-100 text-success-800' :
                    project.status === 'funded' ? 'bg-primary-100 text-primary-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                    {project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}
                  </span>
                </div>
              </div>

              {project.images && project.images.length > 1 && (
                <div className="p-3 sm:p-4">
                  <div className="flex space-x-2 overflow-x-auto">
                    {project.images.map((image: any, index: number) => {
                      const thumbUrl = getImageUrl(image?.image_url);
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 ${selectedImageIndex === index ? 'border-primary-500' : 'border-gray-200'
                            }`}
                        >
                          {thumbUrl ? (
                            <img
                              src={thumbUrl}
                              alt={`${project.title} ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = 'https://via.placeholder.com/150?text=Image';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">No img</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Project Details */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 md:p-8">
              <div className="mb-4 sm:mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">{project.title}</h1>
                <div className="flex items-center text-gray-600 mb-3 sm:mb-4">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="text-base sm:text-lg">{project.location}</span>
                </div>
              </div>

              <div className="prose prose-sm sm:prose-base max-w-none mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Project Overview</h3>
                <p className="text-gray-600 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">{project.full_description}</p>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">Min Investment</p>
                  <p className="text-sm sm:text-lg font-bold text-gray-900">
                    {formatCurrency(parseFloat(project?.rules?.min_allowed_investment || '1000'))}
                  </p>
                </div>
                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-success-600 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">Expected ROI</p>
                  <p className="text-sm sm:text-lg font-bold text-gray-900">{project.expected_roi_percentage}%</p>
                </div>
                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-warning-600 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">Duration</p>
                  <p className="text-sm sm:text-lg font-bold text-gray-900">{project.roi_duration_months} months</p>
                </div>
                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <Target className="h-6 w-6 sm:h-8 sm:w-8 text-danger-600 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">Category</p>
                  <p className="text-sm sm:text-lg font-bold text-gray-900 capitalize">{project.category?.name || 'N/A'}</p>
                </div>
              </div>

              {/* Documents */}
              {project.documents && project.documents.length > 0 && (
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Project Documents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {project.documents.map((doc: any, index: number) => (
                      <div key={index} className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 mr-3 flex-shrink-0" />
                        <span className="text-gray-900 font-medium text-sm sm:text-base">{doc.document_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Investment Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Investment Card */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 sticky top-4 sm:top-6">
              <div className="mb-4 sm:mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600 text-sm sm:text-base">Funding Progress</span>
                  <span className="font-bold text-gray-900 text-sm sm:text-base">{progressPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 mb-3 sm:mb-4">
                  <div
                    className="bg-gradient-to-r from-success-500 to-success-600 h-2 sm:h-3 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Target Amount</span>
                  <span className="font-bold text-gray-900 text-sm sm:text-base">{formatCurrency(currentFundingGoal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Raised Amount</span>
                  <span className="font-bold text-success-600 text-sm sm:text-base">{formatCurrency(currentAmountRaised)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Remaining</span>
                  <span className="font-bold text-gray-900 text-sm sm:text-base">{formatCurrency(remainingAmount)}</span>
                </div>
                {project.deadline && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm sm:text-base">Deadline</span>
                    <span className="font-bold text-gray-900 text-sm sm:text-base">
                      {formatDateUtil(project.deadline)}
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={handleInvestClick}
                disabled={isFullyFunded || !canInvest}
                className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold text-base sm:text-lg transition-colors ${isFullyFunded || !canInvest
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-300'
                  : 'bg-white border-2 border-primary-600 text-primary-600 hover:bg-gray-50'
                  }`}
              >
                {isFullyFunded ? 'Fully Funded' : !canInvest ? 'Not Available' : 'Invest Now'}
              </button>
            </div>


          </div>
        </div>
      </div>

      {/* Investment Modal */}
      {showInvestmentModal && (
        <InvestmentModal
          project={project}
          onClose={() => setShowInvestmentModal(false)}
        />
      )}
    </div>
  );
};

export default ProjectDetails;